import { Product, CartItem, SelectedVariation } from '@/types';
import { useStoreInfo } from '@/lib/store';

export interface DiscountInfo {
  hasDiscount: boolean;
  discountType: 'fixed' | 'percentage';
  discountAmount: number;
  threshold: number;
  currentQuantity: number;
  isEligible: boolean;
  discountValue: number;
  originalPrice: number;
  finalPrice: number;
}

export function calculateProductDiscount(product: Product, quantity: number = 1): DiscountInfo {
  const discountOff = parseFloat(product.discount_off) || 0;
  const discountQty = parseInt(product.discount_qty) || 0;
  const sellingPrice = parseFloat(product.sellingprice);
  
  const hasDiscount = discountOff > 0 && discountQty > 0;
  
  if (!hasDiscount) {
    return {
      hasDiscount: false,
      discountType: 'fixed',
      discountAmount: 0,
      threshold: 0,
      currentQuantity: quantity,
      isEligible: false,
      discountValue: 0,
      originalPrice: sellingPrice,
      finalPrice: sellingPrice
    };
  }

  const isEligible = quantity >= discountQty;
  let discountValue = 0;
  let finalPrice = sellingPrice;

  if (isEligible) {
    if (product.discount_type === 'percentage') {
      discountValue = (sellingPrice * discountOff) / 100;
      finalPrice = sellingPrice - discountValue;
    } else {
      discountValue = discountOff;
      finalPrice = Math.max(0, sellingPrice - discountOff);
    }
  }

  return {
    hasDiscount: true,
    discountType: product.discount_type as 'fixed' | 'percentage',
    discountAmount: discountOff,
    threshold: discountQty,
    currentQuantity: quantity,
    isEligible,
    discountValue,
    originalPrice: sellingPrice,
    finalPrice
  };
}

export function calculateVariationDiscount(
  product: Product, 
  variation: SelectedVariation, 
  allVariations: SelectedVariation[]
): DiscountInfo {
  const variationData = product.variations.find(v => v.variation === variation.variation);

  let variationPrice = variationData ? parseFloat(variationData.variation_amount) : 0;

  // Apply quantity-based pricing if present on variation
  if (variationData?.hasQuantityPricing && variationData?.quantityPricing?.length) {
    const qty = variation.quantity;
    const tiers = variationData.quantityPricing
      .map(t => ({
        min: Number(t.minQuantity || 0),
        max: Number(t.maxQuantity || 0),
        price: Number(t.price || 0)
      }))
      .filter(t => t.price > 0)
      .sort((a, b) => a.min - b.min);

    const matchingTier = tiers.find((tier) => qty >= tier.min && qty <= tier.max);
    if (matchingTier) {
      variationPrice = matchingTier.price;
    } else if (tiers.length > 0) {
      const maxTier = tiers[tiers.length - 1];
      if (qty >= maxTier.max) {
        variationPrice = maxTier.price;
      }
    }
  }

  // Calculate total quantity across all variations for this product
  const totalQuantity = allVariations.reduce((sum, v) => sum + v.quantity, 0);
  
  const discountOff = parseFloat(product.discount_off) || 0;
  const discountQty = parseInt(product.discount_qty) || 0;
  
  const hasDiscount = discountOff > 0 && discountQty > 0;
  
  if (!hasDiscount) {
    return {
      hasDiscount: false,
      discountType: 'fixed',
      discountAmount: 0,
      threshold: 0,
      currentQuantity: totalQuantity,
      isEligible: false,
      discountValue: 0,
      originalPrice: variationPrice,
      finalPrice: variationPrice
    };
  }

  const isEligible = totalQuantity >= discountQty;
  let discountValue = 0;
  let finalPrice = variationPrice;

  if (isEligible) {
    if (product.discount_type === 'percentage') {
      discountValue = (variationPrice * discountOff) / 100;
      finalPrice = variationPrice - discountValue;
    } else {
      discountValue = discountOff;
      finalPrice = Math.max(0, variationPrice - discountOff);
    }
  }

  return {
    hasDiscount: true,
    discountType: product.discount_type as 'fixed' | 'percentage',
    discountAmount: discountOff,
    threshold: discountQty,
    currentQuantity: totalQuantity,
    isEligible,
    discountValue,
    originalPrice: variationPrice,
    finalPrice
  };
}

export function calculateCartItemDiscount(cartItem: CartItem): {
  totalOriginalPrice: number;
  totalDiscountValue: number;
  totalFinalPrice: number;
  discountInfo: DiscountInfo;
} {
  if (cartItem.selectedVariations && cartItem.selectedVariations.length > 0) {
    let totalOriginalPrice = 0;
    let totalDiscountValue = 0;
    let totalFinalPrice = 0;

    // Calculate discount for each variation
    cartItem.selectedVariations.forEach(variation => {
      const discountInfo = calculateVariationDiscount(cartItem, variation, cartItem.selectedVariations!);
      const variationTotal = discountInfo.originalPrice * variation.quantity;
      const variationDiscountTotal = discountInfo.discountValue * variation.quantity;
      const variationFinalTotal = discountInfo.finalPrice * variation.quantity;

      totalOriginalPrice += variationTotal;
      totalDiscountValue += variationDiscountTotal;
      totalFinalPrice += variationFinalTotal;
    });

    // Get discount info from first variation (they all share the same product discount rules)
    const firstVariation = cartItem.selectedVariations[0];
    const discountInfo = calculateVariationDiscount(cartItem, firstVariation, cartItem.selectedVariations);

    return {
      totalOriginalPrice,
      totalDiscountValue,
      totalFinalPrice,
      discountInfo
    };
  } else {
    const discountInfo = calculateProductDiscount(cartItem, cartItem.quantity);
    const totalOriginalPrice = discountInfo.originalPrice * cartItem.quantity;
    const totalDiscountValue = discountInfo.discountValue * cartItem.quantity;
    const totalFinalPrice = discountInfo.finalPrice * cartItem.quantity;

    return {
      totalOriginalPrice,
      totalDiscountValue,
      totalFinalPrice,
      discountInfo
    };
  }
}

export function getEffectiveVariationPrice(product: Product, variation: SelectedVariation, allVariations: SelectedVariation[]): number {
  const discountInfo = calculateVariationDiscount(product, variation, allVariations);
  return discountInfo.finalPrice;
}

export function getEffectiveProductPrice(product: Product, quantity: number): number {
  const discountInfo = calculateProductDiscount(product, quantity);
  return discountInfo.finalPrice;
}

export function formatDiscountDisplay(discountType: 'fixed' | 'percentage', discountAmount: number): string {
  if (discountType === 'percentage') {
    return `${discountAmount}% OFF`;
  } else {
    // Use the same currency formatting as the store
    const { storeInfo } = useStoreInfo.getState();
    // Prefer `currency` (common API field), then legacy `currenCy`, fallback to NGN
    const currency = storeInfo?.currency || storeInfo?.currenCy || 'NGN';
    
    try {
      return `${new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'NGN').toUpperCase(),
      }).format(discountAmount)} OFF`;
    } catch (error) {
      console.error('Invalid currency code:', currency);
      return `$${discountAmount.toFixed(2)} OFF`;
    }
  }
}

export function getVariationPricingType(variation: SelectedVariation): 'quantity-tier' | 'variation' {
  if (variation.hasQuantityPricing && Array.isArray(variation.quantityPricing)) {
    const qty = Number(variation.quantity || 0);
    const match = variation.quantityPricing.find((tier) => {
      const min = Number(tier.minQuantity || 0);
      const max = Number(tier.maxQuantity || 0);
      return qty >= min && qty <= max;
    });
    if (match && Number(match.price) > 0) return 'quantity-tier';
  }
  return 'variation';
}

export function getProductPricingType(product: Product, quantity: number): 'product-discount' | 'standard' {
  const basePrice = Number(product.sellingprice || '0');
  const effectivePrice = getEffectiveProductPrice(product, quantity);
  if (effectivePrice !== basePrice) {
    return 'product-discount';
  }
  return 'standard';
}